'use client';

import { Box, IconButton } from '@chakra-ui/react';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { useQuery } from '@tanstack/react-query';
import productService from '@/_service/product.service';
import Details from './Details';
import Combo from './Combo';
import Slider from './Slider';
import { BackIcon } from '@/_assets/icon-components';
import { getSource } from '@/_utils/platform';
import { usePathname } from '@/i18n/navigation';
import { useTelegramBackButton } from '@/_hooks/useTelegramBackButton';
import { useSafeBackNavigation } from '@/_hooks/useSafeBackNavigation';
import type { IProduct } from '@/entities/product/model/types';
import { FEATURED_BRANDS } from '@/_utils/Constants';
import ProductDetailsSkeleton from './ProductDetailsSkeleton';

const ProductMobile = ({
  slug,
  onSuccess,
  onRedirectToCart,
  shipperId,
  isModal,
  initialProduct,
}: {
  slug: string;
  onSuccess?: () => void;
  onRedirectToCart?: () => void;
  shipperId: string;
  isModal?: boolean;
  initialProduct?: IProduct | null;
}) => {
  const pathname = usePathname();
  const { safeBack } = useSafeBackNavigation();

  const { user, branches, session, tokens } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      tokens: state.auth.tokens,
      branches: state.common.branches,
      session: state.session,
    }),
    shallowEqual,
  );
  const { is_hall, menu_id, order_type } = session;
  const isHall = is_hall === 'true';

  const branch = branches?.[shipperId];

  const selectedMenuId =
    isHall || pathname.endsWith('/menu')
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || '';

  // Server prefetch runs without menu_id; only seed initialData before branch menu is known.
  const shouldUseInitialData = Boolean(initialProduct) && !selectedMenuId;

  const { data, isPending, isFetching, isError } = useQuery({
    queryKey: ['product-by-id', slug, shipperId, selectedMenuId],
    queryFn: () => {
      // Brand pages must use the brand-scoped client_id (mika token), not global user.id.
      const brandClientId = tokens?.[shipperId]?.client_id;
      const clientId =
        brandClientId ||
        (shipperId === FEATURED_BRANDS.yaponamama ? user?.id || '' : '');

      return productService
        .getById(
          slug,
          {
            order_source: getSource({ is_hall }),
            only_delivery: String(order_type?.type === 'delivery'),
            only_self_pickup: String(order_type?.type === 'self-pickup'),
            branch_id: branch?.id || '',
            menu_id: selectedMenuId,
            client_id: clientId,
          },
          shipperId,
        )
        .then((res) => res?.data);
    },
    enabled: Boolean(slug && shipperId),
    initialData: shouldUseInitialData ? (initialProduct ?? undefined) : undefined,
    placeholderData: initialProduct ?? undefined,
  });

  const showDetailsSkeleton = !data && !isError && (isPending || isFetching);

  const { isVisible } = useTelegramBackButton({
    onBack: () => (onSuccess ? onSuccess() : safeBack('/')),
  });

  return (
    <Box
      flex={1}
      display='flex'
      flexDirection='column'
      // minHeight='100dvh'
      color='text'
    >
      {!isVisible && (
        <IconButton
          zIndex={99}
          top={2}
          left={2}
          onClick={() => (onSuccess ? onSuccess() : safeBack('/'))}
          position={'fixed'}
          variant={'subtle'}
          rounded={'full'}
          bg='background.card'
          color='text'
        >
          <BackIcon color='currentColor' />
        </IconButton>
      )}
      <Slider list={[data?.image, ...(data?.gallery || [])]} />
      {showDetailsSkeleton ? (
        <ProductDetailsSkeleton />
      ) : data?.type === 'combo' ? (
        <Combo
          data={data}
          onSuccess={onSuccess}
          onRedirectToCart={onRedirectToCart}
          isModal={isModal}
        />
      ) : (
        <Details
          data={data}
          onSuccess={onSuccess}
          onRedirectToCart={onRedirectToCart}
          isModal={isModal}
        />
      )}
    </Box>
  );
};

export default ProductMobile;
